import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type L from 'leaflet';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import CustomTooltip from 'components/tooltip/CustomTooltip';
import CreateBorderButton from './CreateBorderButton';
import DeleteUpdateBorderButton from './DeleteUpdateBorderButton';
import Button from '@mui/material/Button';
import { OffsetIcon, MapSelectionIcon, SaveIcon, SelectionIcon, DeleteIcon } from 'components/icons';
import type { GridParams } from 'contexts/GridContext';

type BorderType = 'legalBorder' | 'buildableBorder' | 'analysisArea';
type BorderShapeType = 'polygon' | 'rectangle';

interface CadastralBorderActionsProps {
  isReview: boolean;
  gridParams: GridParams;
  setGridParams: (newParams: Record<string, unknown>) => void;
  startDrawing: (shapeType: BorderShapeType | null, borderType?: BorderType | null) => void;
  handleDrawRequest: (shapeType: BorderShapeType, borderType: BorderType) => void;
  handleEditButtonClick: (borderType: BorderType) => void;
  legalBorderRef: MutableRefObject<L.Polygon | null>;
  buildableBorderRef: MutableRefObject<L.Polygon | null>;
  analysisAreaRef: MutableRefObject<L.Polygon | null>;
  setShowEditableCoordinatesArea: Dispatch<SetStateAction<boolean>>;
  enableRotateMode: (borderType: BorderType) => void;
  enableDragMode: (borderType: BorderType) => void;
  activeEditBorderType: BorderType | null;
  handleLinkedAnalysisAreaDelete: (deletedBorderType: BorderType) => void;
  handleOpenModalForAnalysisAreaType: () => void;
  centerOfAnalysisAreaRef: MutableRefObject<L.Marker | null>;
  isAnalysisAreaLinked: boolean;
  isGridCoordinateSystemValid: boolean;
  analysisAreaLinkedLabel: string;
  hasLinkedBorderOptions: boolean;
  isLegalBorderParcelMode?: boolean;
  onToggleLegalBorderParcelMode: () => void;
  legalBorderParcelSelectionCount?: number;
  onSaveLegalBorderParcelSelection: () => void;
  isLegalBorderParcelModeUnavailable?: boolean;
  isBuildableOffsetModeActive?: boolean;
  onToggleBuildableOffsetMode: () => void;
  onBeforeBorderAction: () => void;
  renderCoordinateEditor?: (borderType: BorderType) => React.ReactNode;
  analysisAreaCount: number;
  activeAnalysisAreaIndex: number;
  maxAnalysisAreas: number;
  allVolumesHaveAnalysisArea: boolean;
  onSelectPreviousAnalysisArea: () => void;
  onSelectNextAnalysisArea: () => void;
  onAddAnalysisArea: () => void;
  onDeleteActiveAnalysisArea: () => void;
  onDeleteActiveLegalBorder: () => void;
  onDeleteActiveBuildableBorder: () => void;
  onDeleteVolume: (volumeIndex: number) => void;
}

const CadastralBorderActions = ({
  isReview,
  gridParams,
  setGridParams,
  startDrawing,
  handleDrawRequest,
  handleEditButtonClick,
  legalBorderRef,
  buildableBorderRef,
  analysisAreaRef,
  setShowEditableCoordinatesArea,
  enableRotateMode,
  enableDragMode,
  activeEditBorderType,
  handleLinkedAnalysisAreaDelete,
  handleOpenModalForAnalysisAreaType,
  centerOfAnalysisAreaRef,
  isAnalysisAreaLinked,
  isGridCoordinateSystemValid,
  analysisAreaLinkedLabel,
  hasLinkedBorderOptions,
  isLegalBorderParcelMode = false,
  onToggleLegalBorderParcelMode,
  legalBorderParcelSelectionCount = 0,
  onSaveLegalBorderParcelSelection,
  isLegalBorderParcelModeUnavailable = false,
  isBuildableOffsetModeActive = false,
  onToggleBuildableOffsetMode,
  onBeforeBorderAction,
  renderCoordinateEditor,
  analysisAreaCount,
  activeAnalysisAreaIndex,
  maxAnalysisAreas,
  allVolumesHaveAnalysisArea,
  onSelectPreviousAnalysisArea,
  onSelectNextAnalysisArea,
  onAddAnalysisArea,
  onDeleteActiveAnalysisArea,
  onDeleteActiveLegalBorder,
  onDeleteActiveBuildableBorder,
  onDeleteVolume,
}: CadastralBorderActionsProps) => {
  const offsetActionIcon = <OffsetIcon className="action-icons" style={{ color: '#fff', width: 18, height: 18 }} />;
  const selectionActionIcon = (
    <SelectionIcon className="action-icons" style={{ color: '#fff', width: 18, height: 18 }} />
  );
  const hasActiveAnalysisArea = gridParams.analysisArea.length > 0;
  const hasMultipleAnalysisAreas = analysisAreaCount > 1;
  const showInactiveVolumeState = !hasActiveAnalysisArea && !hasMultipleAnalysisAreas && activeAnalysisAreaIndex === 0;
  const showAddVolumeButton = !isReview && allVolumesHaveAnalysisArea && analysisAreaCount < maxAnalysisAreas;
  const showDeleteVolumeButton = !isReview && hasMultipleAnalysisAreas;

  const renderBorderSection = (
    borderType: BorderType,
    label: React.ReactNode,
    tooltipTitle: string | undefined,
    controls: React.ReactNode,
    sectionClassName = ''
  ) => (
    <div className={`grid-border-section ${sectionClassName}`.trim()}>
      <div className="grid-border-section__main">
        <div className="grid-border-section__label">
          <span>{label}</span>
          {tooltipTitle ? <CustomTooltip title={tooltipTitle} arrow /> : null}
        </div>
        <div className="grid-border-section__controls">{controls}</div>
      </div>
      {renderCoordinateEditor?.(borderType)}
    </div>
  );

  const parcelSelectionButton = (
    <Button
      size="small"
      color="secondary"
      disabled={isReview || isLegalBorderParcelModeUnavailable}
      className={`border-action-icon-white ${isLegalBorderParcelMode ? 'save-button-active' : ''} ${
        isReview
          ? 'border-action-disabled'
          : isLegalBorderParcelModeUnavailable
            ? 'border-action-disabled border-action-disabled--secondary'
            : ''
      }`.trim()}
      data-border-action="parcel-selection"
      onClick={
        isLegalBorderParcelMode && legalBorderParcelSelectionCount > 0
          ? onSaveLegalBorderParcelSelection
          : onToggleLegalBorderParcelMode
      }
    >
      {isLegalBorderParcelMode && legalBorderParcelSelectionCount > 0 ? (
        <SaveIcon style={{ color: '#fff' }} />
      ) : (
        <MapSelectionIcon style={{ color: '#fff' }} />
      )}
    </Button>
  );

  const buildableOffsetSelectionButton = (
    <Button
      size="small"
      color="warning"
      disabled={isReview}
      className={`border-action-icon-white ${isBuildableOffsetModeActive ? 'save-button-active' : ''} ${isReview ? 'border-action-disabled' : ''}`.trim()}
      data-border-action="buildable-offset-selection"
      onClick={onToggleBuildableOffsetMode}
    >
      {offsetActionIcon}
    </Button>
  );

  return (
    <>
      <div className="analysis-area-volume-field__main">
        <div className="analysis-area-volume-field__title text-sm font-semibold">Define Grid Analysis Volume</div>
        <div className="analysis-area-volume-selector analysis-area-volume-selector--top">
          <div
            className={`analysis-area-volume-selector__pill ${showInactiveVolumeState ? 'analysis-area-volume-selector__pill--disabled' : ''}`.trim()}
            aria-disabled={showInactiveVolumeState}
          >
            {hasMultipleAnalysisAreas ? (
              <button
                type="button"
                className="analysis-area-volume-selector__pill-nav"
                onClick={onSelectPreviousAnalysisArea}
                disabled={showInactiveVolumeState}
                aria-label="Previous analysis volume"
              >
                <ChevronLeftIcon fontSize="small" />
              </button>
            ) : null}
            <span className="analysis-area-volume-selector__pill-label">
              {`Volume ${analysisAreaCount === 0 ? 1 : activeAnalysisAreaIndex + 1}`}
            </span>
            {hasMultipleAnalysisAreas ? (
              <button
                type="button"
                className="analysis-area-volume-selector__pill-nav"
                onClick={onSelectNextAnalysisArea}
                disabled={showInactiveVolumeState}
                aria-label="Next analysis volume"
              >
                <ChevronRightIcon fontSize="small" />
              </button>
            ) : null}
          </div>
          {showAddVolumeButton ? (
            <Button
              size="small"
              className="analysis-area-volume-selector__circle"
              data-border-action="analysis-add"
              onClick={onAddAnalysisArea}
              aria-label="Add analysis volume"
            >
              <AddIcon />
            </Button>
          ) : null}
          {showDeleteVolumeButton ? (
            <Button
              size="small"
              className="analysis-area-volume-selector__circle"
              data-border-action="analysis-volume-delete"
              onClick={() => onDeleteVolume(activeAnalysisAreaIndex)}
              aria-label={`Delete Volume ${activeAnalysisAreaIndex + 1}`}
            >
              <DeleteIcon style={{ width: '1.1rem', height: '1.1rem' }} />
            </Button>
          ) : null}
        </div>
      </div>

      {renderBorderSection(
        'legalBorder',
        <>
          Property Area <span className="grid-border-section__optional">(optional)</span>
        </>,
        'Select or draw on the map',
        gridParams.legalBorder.length === 0 ? (
          <CreateBorderButton
            isReview={isReview}
            color="secondary"
            startDrawing={startDrawing}
            onDrawRequest={handleDrawRequest}
            borderType="legalBorder"
            btnText="Property Area"
            leadingActionButton={parcelSelectionButton}
            onBeforeAction={onBeforeBorderAction}
            hideLabelChip
          />
        ) : (
          <DeleteUpdateBorderButton
            isReview={isReview}
            setGridParams={setGridParams}
            handleEditButtonClick={handleEditButtonClick}
            borderType="legalBorder"
            borderTypeRef={legalBorderRef}
            setShowEditableCoordinatesArea={setShowEditableCoordinatesArea}
            btnText="Property Area"
            color="secondary"
            enableRotateMode={enableRotateMode}
            enableDragMode={enableDragMode}
            isEditing={activeEditBorderType === 'legalBorder'}
            onDelete={handleLinkedAnalysisAreaDelete}
            onBeforeAction={onBeforeBorderAction}
            hideLabelChip
            onCustomDelete={onDeleteActiveLegalBorder}
          />
        )
      )}

      {renderBorderSection(
        'buildableBorder',
        <>
          Buildable Area <span className="grid-border-section__optional">(optional)</span>
        </>,
        'Offset from the property area or draw on the map',
        gridParams.buildableBorder.length === 0 ? (
          <CreateBorderButton
            isReview={isReview}
            color="warning"
            startDrawing={startDrawing}
            onDrawRequest={handleDrawRequest}
            borderType="buildableBorder"
            btnText="Buildable Area"
            showSelectButton={gridParams.legalBorder.length > 0}
            handleSelectClick={onToggleBuildableOffsetMode}
            isSelectActive={isBuildableOffsetModeActive}
            selectActionIcon={offsetActionIcon}
            onBeforeAction={onBeforeBorderAction}
            hideLabelChip
          />
        ) : (
          <DeleteUpdateBorderButton
            isReview={isReview}
            setGridParams={setGridParams}
            handleEditButtonClick={handleEditButtonClick}
            borderType="buildableBorder"
            borderTypeRef={buildableBorderRef}
            setShowEditableCoordinatesArea={setShowEditableCoordinatesArea}
            btnText="Buildable Area"
            color="warning"
            enableRotateMode={enableRotateMode}
            enableDragMode={enableDragMode}
            isEditing={activeEditBorderType === 'buildableBorder'}
            onDelete={handleLinkedAnalysisAreaDelete}
            leadingActionButton={gridParams.legalBorder.length > 0 ? buildableOffsetSelectionButton : null}
            onBeforeAction={onBeforeBorderAction}
            hideLabelChip
            onCustomDelete={onDeleteActiveBuildableBorder}
          />
        )
      )}

      {renderBorderSection(
        'analysisArea',
        <>
          Analysis Area <span style={{ color: '#d32f2f' }}>*</span>
        </>,
        'Select from the Buildable or Property Area, or draw on the map',
        <div className="analysis-area-field">
          <div className="analysis-area-field__actions">
            {gridParams.analysisArea.length === 0 ? (
              <CreateBorderButton
                isReview={isReview}
                color="error"
                startDrawing={startDrawing}
                onDrawRequest={handleDrawRequest}
                borderType="analysisArea"
                btnText="Analysis Area *"
                handleOpenModalForAnalysisAreaType={handleOpenModalForAnalysisAreaType}
                showSelectButton={hasLinkedBorderOptions}
                selectActionIcon={selectionActionIcon}
                onBeforeAction={onBeforeBorderAction}
                hideLabelChip
              />
            ) : (
              <DeleteUpdateBorderButton
                isReview={isReview}
                setGridParams={setGridParams}
                handleEditButtonClick={handleEditButtonClick}
                borderType="analysisArea"
                borderTypeRef={analysisAreaRef}
                setShowEditableCoordinatesArea={setShowEditableCoordinatesArea}
                btnText="Analysis Area"
                color="error"
                enableRotateMode={enableRotateMode}
                enableDragMode={enableDragMode}
                centerOfAnalysisAreaRef={centerOfAnalysisAreaRef}
                isEditing={activeEditBorderType === 'analysisArea'}
                onDelete={handleLinkedAnalysisAreaDelete}
                editButtonDisabled={
                  isAnalysisAreaLinked || (activeEditBorderType === 'analysisArea' && !isGridCoordinateSystemValid)
                }
                editButtonTooltip={
                  isAnalysisAreaLinked
                    ? `The analysis area follows the changes of the ${analysisAreaLinkedLabel} area.`
                    : activeEditBorderType === 'analysisArea' && !isGridCoordinateSystemValid
                      ? 'Fix custom coordinates errors before saving the analysis area.'
                      : undefined
                }
                onCustomDelete={onDeleteActiveAnalysisArea}
                onBeforeAction={onBeforeBorderAction}
                hideLabelChip
              />
            )}
          </div>
        </div>,
        'grid-border-section--analysis-area'
      )}
    </>
  );
};

export default CadastralBorderActions;
